export {Song as Page};

import {useContext, useEffect, useRef, useState} from "preact/hooks";
import {Helmet} from "react-helmet-async";
import API_URL, {withSite} from "../../includes/api.js";
import {NotFound} from '../../components/NotFound.jsx';
import {LoginDialog} from "../../components/LoginDialog.jsx";
import {PlaylistsList} from "../../components/PlaylistsList.jsx";
import ReactPlayer from 'react-player/youtube';
import {reportSong} from "../../includes/requests.js";
import {useAuth} from '../../components/AuthProvider.jsx';
import {
    ArrowPathIcon,
    ArrowsRightLeftIcon,
    ArrowUpTrayIcon,
    HeartIcon,
    PlayIcon
} from "@heroicons/react/24/solid";
import {ExclamationTriangleIcon} from "@heroicons/react/24/outline/index.js";
import {Song as SongComponent} from "../../components/Song.jsx";
import {useLocation} from "../../hooks/useLocation.js";
import {navigate} from "vike/client/router";
import {usePageContext} from "../../hooks/usePageContext.js";
import {ClientOnly} from "../../components/ClientOnly.jsx";
import {getRequestDomain} from "../../../tenant.config.js";

function Song() {
    const playerRef = useRef(null);
    const popupRef = useRef();

    const pageContext = usePageContext();
    const location = useLocation();
    const search = pageContext?.urlParsed?.search ?? {};
    // Seeded from pages/song/+data.shared.js's server fetch so <Helmet>/first paint show the real song.
    // Only set on the first (SSR) load; after client-side navigation it's null and the fetch below loads the song.
    const initialSong = pageContext?.data?.song ?? null;
    // Alias-aware: staging (erocthgin.com) canonicalizes to itself, not to prod.
    const domain = getRequestDomain(pageContext?.hostname);

    const {getToken, user, authLoading} = useAuth();

    const initialPlaylist = search.p ?? '';

    const [loading, setLoading] = useState(!initialSong);
    const [s, setS] = useState(search.s ?? '');
    const [currentPlaylist, setCurrentPlaylist] = useState(initialPlaylist);
    const [shuffle, setShuffle] = useState(!!search.shuffle);
    const [repeat, setRepeat] = useState(false);
    const [song, setSong] = useState(initialSong ?? []);
    // The player follows the URL immediately; everything built from `song` waits for its fetch.
    const [loadedSongId, setLoadedSongId] = useState(initialSong ? (search.s ?? '') : null);
    const songPending = loadedSongId !== (search.s ?? '');
    const controlsDisabled = loading || songPending;

    const [showDialog, setShowDialog] = useState(false);
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [favoriteLoading, setFavoriteLoading] = useState(false);
    // A null seed means client-side navigation or a server that couldn't fetch
    // (API unreachable, rate-limited, or an auth-only playlist), so the fetch
    // below decides; starting in the not-found state would flash a 404 body.
    const [is404, setIs404] = useState(s === '');

    const [playlistsPopoverOpen, setPlaylistsPopoverOpen] = useState(false);
    const [userPlaylists, setUserPlaylists] = useState([]);
    const [playlistsLoading, setPlaylistsLoading] = useState(false);

    useEffect(() => {
        if (window.location.pathname !== '/song') return;
        const params = new URLSearchParams(window.location.search);
        setS(params.get('s') ?? '');
        setCurrentPlaylist(params.get('p') ?? '');
        setShuffle(!!params.get('shuffle'));
    }, [search.s, search.p, search.shuffle]);

    // Every change of song goes through Vike's router rather than a manual
    // history.pushState(). The page used to push '/song?s=..&p=..' itself on
    // every mount and song change, which (a) duplicated the entry Vike had
    // just created for the navigation, and (b) marked the entry as
    // user-pushed, so on Back Vike fired onPageTransitionStart, saw a
    // same-page user entry, and returned without rendering or firing
    // onPageTransitionEnd: the loading bar stayed up and nothing changed.
    // With Vike owning the entry, Back re-renders the previous URL and the
    // search.s effect above picks up the song.
    const goToSong = (id, {shuffle: withShuffle = shuffle} = {}) => {
        let url = '/song?s=' + id;
        if (currentPlaylist) url += '&p=' + currentPlaylist;
        if (withShuffle) url += '&shuffle=1';
        navigate(url, {keepScrollPosition: true});
    };

    useEffect(() => {
        if (authLoading) return;

        // song/+data.shared.js already fetched exactly this request on the server.
        const seeded = Boolean(initialSong && initialSong.youtube_url === s);
        if (seeded) {
            setSong(initialSong);
            setLoadedSongId(s);
            setIs404(false);
            setLoading(false);
            if (currentPlaylist === '') {
                setCurrentPlaylist(initialSong.category_id);
            }
        }

        if (!user) {
            if (!seeded) getSong();
            return;
        }

        if (!currentPlaylist.includes('custom-') && !currentPlaylist.includes('favorite')) {
            getSong({background: seeded});
            return;
        }

        const endpoint = currentPlaylist.includes('custom-') ? 'playlists' : 'favorite';
        const requestedSong = s;
        latestSongRequest.current = requestedSong;
        getToken().then(token => {
            fetch(`${API_URL}/${endpoint}/get?s=${s}&p=${currentPlaylist}&firebase_auth_token=${token}`)
                .then(response => response.json())
                .then(data => {
                    if (latestSongRequest.current !== requestedSong) return;
                    if (data.code === 200) {
                        setSong(data.data);
                        setLoadedSongId(requestedSong);
                        setLoading(false);
                        setIs404(false);
                    } else {
                        setIs404(true);
                    }
                });
        });
    }, [authLoading, user, s]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (playlistsPopoverOpen && popupRef.current && !popupRef.current.contains(event.target)) {
                setPlaylistsPopoverOpen(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [playlistsPopoverOpen]);

    const handleHeartClick = () => {
        setPlaylistsPopoverOpen((open) => {
            if (!open) fetchUserPlaylists();
            return !open;
        });
    };

    // Invalidate playlist cache when the song changes
    const playlistsFetchedAt = useRef(0);
    const lastSongId = useRef(s);
    useEffect(() => {
        if (lastSongId.current !== s) {
            playlistsFetchedAt.current = 0;
            lastSongId.current = s;
            setPlaylistsPopoverOpen(false);
        }
    }, [s]);

    // Fetch playlists when popover opens (cached for 30 seconds)
    const fetchUserPlaylists = async () => {
        if (playlistsLoading) return;
        if (Date.now() - playlistsFetchedAt.current < 30_000 && userPlaylists.length > 0) return;
        setPlaylistsLoading(true);
        const token = await getToken();
        fetch(`${API_URL}/playlists/list?s=${s}&firebase_auth_token=${token}`)
            .then(response => response.json())
            .then(data => {
                setUserPlaylists(data?.data);
                playlistsFetchedAt.current = Date.now();
            })
            .finally(() => setPlaylistsLoading(false));
    };

    const toggleToPlaylist = async (playlistId) => {
        const token = await getToken();
        const formData = new FormData();
        formData.append('firebase_auth_token', token);
        formData.append('s', s);
        formData.append('playlist_id', playlistId);

        fetch(API_URL + '/playlists/toggle', {
            method: 'POST',
            body: formData
        })
            .then(response => response.json())
            .then(data => {
                setUserPlaylists(prev =>
                    prev.map(pl =>
                        pl.id === playlistId
                            ? {...pl, is_active_song: !pl.is_active_song}
                            : pl
                    )
                );
            });
    };

    const latestSongRequest = useRef(null);

    const getSong = async ({background = false} = {}) => {
        const requestedSong = s;
        latestSongRequest.current = requestedSong;

        let params = 's=' + s;
        if (currentPlaylist !== '') {
            params += '&p=' + currentPlaylist;
        }
        params += '&explore=true';

        if (!background) setLoading(true);
        const token = await getToken();
        if (token) {
            params += '&firebase_auth_token=' + token;
        }
        fetch(withSite(API_URL + "/song/get?" + params))
            .then(response => response.json()).then(data => {
            if (latestSongRequest.current !== requestedSong) return;
            if (data.code === 200) {
                setSong(data.data);
                setLoadedSongId(requestedSong);
                setIs404(false);
                if (currentPlaylist === '') {
                    setCurrentPlaylist(data.data.category_id);
                }
            } else if (!background) {
                setIs404(true);
            }
            setLoading(false);
        });
    }

    const copyUrl = (e) => {
        setPopoverOpen(true);
        navigator.clipboard.writeText(window.location.origin + '/song?s=' + s);
        e.target.classList.add('fill-red-400');
        setTimeout(() => {
            e.target.classList.remove('fill-red-400');
            setPopoverOpen(false);
        }, 1500);
    }

    const toggleFavorite = async () => {
        if (favoriteLoading) return;

        setFavoriteLoading(true);
        const token = await getToken();
        const formData = new FormData();
        formData.append('firebase_auth_token', token);
        formData.append('s', s);

        const action = song.favorite ? 'remove' : 'add';
        fetch(API_URL + '/favorite/' + action, {
            method: 'POST',
            body: formData
        })
            .then(response => response.json())
            .then(data => {
                if (data.code === 200) {
                    const addFavoriteButton = document.getElementById('add-favorite');
                    addFavoriteButton.classList.toggle('fill-red-400');
                    setSong({...song, favorite: !song.favorite});
                }
            })
            .finally(() => {
                setFavoriteLoading(false);
            });
    }

    const handleEnded = async () => {
        if (repeat) {
            playerRef.current.getInternalPlayer().playVideo();
        } else if (shuffle) {
            const token = await getToken();
            let endpoint = "/song/random?p=" + currentPlaylist;
            if (currentPlaylist === 'favorite') {
                endpoint = "/favorite/random?firebase_auth_token=" + token;
            }
            if (currentPlaylist.startsWith('custom-')) {
                endpoint = "/playlists/random?p=" + currentPlaylist + "&firebase_auth_token=" + token;
            }
            fetch(API_URL + endpoint)
                .then(response => response.json())
                .then(data => {
                    if (data.code === 200) {
                        setShuffle(true);
                        goToSong(data.data.youtube_url, {shuffle: true});
                    }
                });
        } else if (song.next) {
            goToSong(song.next);
        }
    };

    if (is404) {
        return <NotFound/>;
    }

    return (
        <>
            <Helmet>
                <title>{song.title}</title>
                <meta name="description" content={`Listen to ${song.title}`}/>
                <meta property="og:image" content={`https://i1.ytimg.com/vi_webp/${s}/mqdefault.webp`}/>
                {/* Never includes p= — the song itself doesn't depend on which playlist it was reached through. */}
                <link rel="canonical" href={`https://www.${domain}/song?s=${s}`}/>
            </Helmet>

            <LoginDialog showDialog={showDialog} setShowDialog={setShowDialog}/>

            <section className="text-white">

                <div className="min-h-dvh">

                    <div className="flex flex-wrap md:flex-nowrap gap-5 mb-8">
                        <ClientOnly fallback={
                            <div className="w-full rounded-lg border border-border/60 bg-zinc-800 rounded-md md:w-2/3 lg:w-3/4 h-(--h-35vh) md:h-(--h-100) lg:h-(--h-120)"/>
                        }>
                            <ReactPlayer
                                ref={playerRef}
                                className={"w-full rounded-lg border border-border/60 bg-zinc-800 group rounded-md md:w-2/3 lg:w-3/4 h-(--h-35vh) md:h-(--h-100) lg:h-(--h-120)"}
                                url={`https://www.youtube-nocookie.com/watch?v=${s}`}
                                playing={true}
                                controls={true}
                                playIcon={<PlayIcon
                                    className="w-12 h-12 hover:scale-120 transition-transform duration-300 text-white z-1 group-hover:scale-120"/>}
                                light={`https://i1.ytimg.com/vi/${s}/hqdefault.jpg`}
                                width="revert-layer"
                                height="revert-layer"
                                onReady={(player) => {
                                    player.getInternalPlayer().playVideo();
                                }}
                                onEnded={handleEnded}
                                config={{
                                    youtube: {
                                        playerVars: {
                                            autoplay: 1,
                                            origin: typeof window !== 'undefined' ? window.location.origin : undefined,
                                            modestbranding: 1,
                                            fs: 0,
                                            playsinline: 1,
                                            iv_load_policy: 3,
                                            rel: 0,
                                            disablekb: 1,
                                        }
                                    }
                                }}
                            />
                        </ClientOnly>
                        <div className="w-full md:w-1/3 lg:w-1/4 flex flex-col justify-between gap-4">

                            <div className="order-1 md:order-0">
                                {songPending ? (
                                    <div className="h-40 w-full rounded-lg border border-border/60 bg-zinc-800 animate-pulse"/>
                                ) : song.next ? (
                                    <SongComponent
                                        song={{
                                            category_name: 'Next song',
                                            category_id: currentPlaylist,
                                            author_name: '',
                                            title: song.next_title,
                                            youtube_url: song.next,
                                            date_added: '',
                                        }}
                                        small="true"
                                        onSongClick={() => goToSong(song.next)}
                                    />
                                ) : (
                                    <div className="h-40 w-full rounded-lg border border-border/60 bg-zinc-800">
                                        <div className="text-sm flex items-center justify-center h-full text-gray-400">
                                            No next song
                                        </div>
                                    </div>
                                )}
                            </div>

                            {!songPending && song.sponsor && (
                                <div className="order-2">
                                    <SongComponent
                                        song={song.sponsor}
                                        small="true"
                                        onSongClick={() => location.route("/song?s=" + song.sponsor.youtube_url + (song.sponsor.category_id ? '&p=' + song.sponsor.category_id : ''))}
                                    />
                                    <span className="text-xs text-gray-400 italic">Your ad here? <a href="/subscribe"
                                                                                                    className="text-gray-400 underline">Subscribe</a></span>
                                </div>
                            )}

                            {/* Video controls */}
                            <div
                                className="order-0 md:order-3 flex justify-around p-2 rounded-lg border shadow-sm bg-zinc-800 border-border/60"
                                role="toolbar"
                                aria-label="Playback controls">
                                <div className="relative inline-block">
                                    <button
                                        type="button"
                                        onClick={() => user ? handleHeartClick() : setShowDialog(true)}
                                        aria-label={!songPending && song.favorite ? "Remove from favorites / playlists" : "Add to favorites / playlists"}
                                        className="flex items-center"
                                    >
                                        <HeartIcon
                                            id='add-favorite'
                                            className={`w-5 h-5 hover:scale-120 transition-transform duration-300 cursor-pointer transition-colors duration-200 ${!songPending && song.favorite ? 'fill-red-400' : 'fill-white'}`}
                                            aria-hidden="true"
                                        />
                                    </button>
                                    {playlistsPopoverOpen && (
                                        <div ref={popupRef}
                                             className="absolute z-20 top-6 dropdown-account bg-white divide-y divide-gray-100 rounded-lg shadow-sm w-44 py-2 text-sm text-gray-700 list-none">
                                            <button
                                                className="absolute top-1.5 right-2 text-gray-400 hover:text-white text-lg font-bold cursor-pointer"
                                                onClick={() => setPlaylistsPopoverOpen(false)}
                                                aria-label="Close"
                                            >
                                                ×
                                            </button>
                                            <div className="px-4 pb-1 font-semibold text-lg border-none">Add to
                                                playlist
                                            </div>
                                            <div>
                                                <label htmlFor="favorite-checkbox"
                                                       className="flex px-4 py-2 hover:bg-gray-100 cursor-pointer items-center w-full">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!song.favorite}
                                                        onChange={toggleFavorite}
                                                        className="mr-2 cursor-pointer accent-red-400"
                                                        id="favorite-checkbox"
                                                    />
                                                    <span
                                                        className={`${song.favorite ? "text-red-400" : "text-gray-900"}`}>Favorite</span>
                                                </label>
                                                {/* Other playlists */}
                                                {playlistsLoading ? (
                                                    <div className="text-gray-400 px-4 py-2">Loading...</div>
                                                ) : userPlaylists.length === 0 ? (
                                                    <></>
                                                ) : (
                                                    userPlaylists
                                                        .filter(pl => pl.id !== 'favorite')
                                                        .map(pl => (
                                                            <div key={pl.id}
                                                                 className="flex px-4 py-2 hover:bg-gray-100 cursor-pointer">
                                                                <label
                                                                    htmlFor={`playlist-checkbox-${pl.id}`}
                                                                    className={`flex items-center w-full ${pl.is_active_song ? "text-red-400" : "text-gray-900"}`}
                                                                    style={{cursor: "pointer"}}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={!!pl.is_active_song}
                                                                        onChange={() => toggleToPlaylist(pl.id)}
                                                                        className="mr-2 cursor-pointer accent-red-400"
                                                                        id={`playlist-checkbox-${pl.id}`}
                                                                    />
                                                                    {pl.title}
                                                                </label>
                                                            </div>
                                                        ))
                                                )}
                                            </div>
                                            <a href="/account/playlists"
                                               className="block px-4 cursor-pointer py-2 text-sm text-gray-900 hover:bg-gray-100">
                                                Create playlist
                                            </a>
                                        </div>
                                    )}
                                </div>

                                <button type="button"
                                        aria-label="Previous song"
                                        disabled={!(song.previous && !controlsDisabled)}
                                        onClick={() => (song.previous && !controlsDisabled) ? goToSong(song.previous) : undefined}
                                        className="flex items-center disabled:opacity-60">
                                    <PlayIcon className={`w-5 h-5 hover:scale-120 transition-transform duration-300 rotate-180 ${(song.previous && !controlsDisabled) ? "text-white" : "text-gray-400"}`} aria-hidden="true"/>
                                </button>

                                <button type="button"
                                        aria-label="Next song"
                                        disabled={!(song.next && !controlsDisabled)}
                                        onClick={() => (song.next && !controlsDisabled) ? goToSong(song.next) : undefined}
                                        className="flex items-center disabled:opacity-60">
                                    <PlayIcon className={`w-5 h-5 hover:scale-120 transition-transform duration-300 ${(song.next && !controlsDisabled) ? "text-white" : "text-gray-400"}`} aria-hidden="true"/>
                                </button>

                                <button type="button"
                                        aria-label={shuffle ? "Shuffle on" : "Shuffle off"}
                                        aria-pressed={shuffle}
                                        onClick={() => setShuffle(!shuffle)}
                                        className="flex items-center">
                                    <ArrowsRightLeftIcon className={`w-5 h-5 hover:scale-120 transition-transform duration-300 ${!shuffle ? "fill-white" : "fill-red-400"}`} aria-hidden="true"/>
                                </button>

                                <button type="button"
                                        aria-label={repeat ? "Repeat on" : "Repeat off"}
                                        aria-pressed={repeat}
                                        onClick={() => setRepeat(!repeat)}
                                        className="flex items-center">
                                    <ArrowPathIcon className={`w-5 h-5 hover:scale-120 transition-transform duration-300 ${repeat ? "fill-red-400" : "fill-white"}`} aria-hidden="true"/>
                                </button>

                                <button type="button"
                                        aria-label="Report song"
                                        onClick={() => reportSong(s)}
                                        className="flex items-center">
                                    <ExclamationTriangleIcon className="w-5 h-5 hover:scale-120 transition-transform duration-300" aria-hidden="true"/>
                                </button>

                                <div className="relative">
                                    <button type="button"
                                            aria-label="Copy link to song"
                                            onClick={(e) => copyUrl(e)}
                                            className="flex items-center">
                                        <ArrowUpTrayIcon className="w-5 h-5 hover:scale-120 transition-transform duration-300 fill-white" aria-hidden="true"/>
                                    </button>
                                    {popoverOpen && (
                                        <div
                                            className="popover-content absolute text-xs text-gray-500 bg-white border border-gray-200 rounded-lg shadow-xs">
                                            <div className="py-2 px-3">
                                                Link&nbsp;copied!
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {songPending ? (
                        <div className="animate-pulse" aria-busy="true" aria-label="Loading song">
                            <div className="mb-8">
                                <div className="h-7 w-2/3 max-w-xl rounded bg-zinc-800"/>
                                <div className="mt-2 flex gap-2">
                                    <div className="h-4 w-20 rounded-lg bg-zinc-800"/>
                                    <div className="h-4 w-24 rounded-lg bg-zinc-800"/>
                                </div>
                            </div>
                            <div className="h-6 w-40 mb-2 rounded bg-zinc-800"/>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                {[0, 1, 2, 3].map((i) => (
                                    <div key={i} className="h-[71px] rounded-lg border border-border/60 bg-zinc-800"/>
                                ))}
                            </div>
                            <div className="h-6 w-32 mt-6 mb-2 rounded bg-zinc-800"/>
                            <div className="space-y-2">
                                <div className="h-3 w-full rounded bg-zinc-800"/>
                                <div className="h-3 w-5/6 rounded bg-zinc-800"/>
                                <div className="h-3 w-2/3 rounded bg-zinc-800"/>
                            </div>
                        </div>
                    ) : (
                        <>
                        <div className="mb-8">
                            <h1 className="text-xl">{song.title}</h1>
                            {song.new === 'true' ? <span
                                className="bg-red-400 text-gray-300 text-xs px-2 py-0.5 rounded-lg mr-2">New</span> : null}
                            {song.author_name && (<a href={`/search?q=${encodeURIComponent(song.author_name)}&type=artist`}
                                                     className="bg-gray-700 text-white text-xs px-2 py-0.5 rounded-lg mr-2">{song.author_name}</a>)}
                            {song.category_name && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (song.first_song) {
                                            location.route(`/song?s=${song.first_song}&p=${song.category_id}`);
                                        }
                                    }}
                                    className="cursor-pointer bg-blue-600 text-white text-xs px-2 py-0.5 rounded-lg mr-2"
                                    aria-label={`Browse playlist: ${song.category_name}`}
                                >
                                    {song.category_name}
                                </button>
                            )}
                        </div>

                        {song.explore && song.explore.length > 0 && (
                            <>
                                <div className="text-lg mb-2 font-bold uppercase">Explore more</div>
                                <PlaylistsList playlists={song.explore} location={location}/>
                            </>
                        )}

                        {song.description && (
                            <>
                                <div className="text-lg mt-6 mb-2 font-bold uppercase">Description</div>
                                <div className="song-description text-sm break-all">
                                    {song.description.split('\n').map((line, index) => {
                                        // replace links with clickable links that opens in new tab
                                        const htmlLine = line.replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank">$1</a>');
                                        return (
                                            <>
                                                <span dangerouslySetInnerHTML={{__html: htmlLine}}/>
                                                <br/>
                                            </>
                                        )
                                    })}
                                </div>
                            </>
                        )}
                        </>
                    )}
                </div>
            </section>
        </>
    );
}
